import { AuthProvider } from "@pankod/refine-core";
import { TOKEN_KEY, USER_DATA, ADMIN_BFF_URL, BFF_KEY } from "./environment";
import { AxiosInstance } from "axios";
import dayjs from "dayjs";

export interface IUserData {
  id: string;
  name: string;
  roles: string;
  lastLogin: string;
}

const config = {
  headers: {
    "Content-Type": "application/json",
    "ocp-apim-subscription-key": BFF_KEY,
  },
};

export function handleLogout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_DATA);
}

export const authProvider = (axiosInstance: AxiosInstance): AuthProvider => ({
  login: async ({ email, secret }) => {
    try {
      const { data } = await axiosInstance.post(
        `${ADMIN_BFF_URL}/v1/auth/login`,
        {
          email,
          secret,
        },
        config
      );

      if (!data.access_token) {
        throw new Error("Token não recebido do servidor");
      }

      const accessToken = data.access_token;
      const [, payloadBase64] = accessToken.split(".");
      const payload = JSON.parse(atob(payloadBase64));

      const userId =
        payload?.sub || payload?.id || payload?.user_id || payload?.userid;

      if (!userId) {
        console.error(
          "ID do usuário não encontrado no token. Campos disponíveis:",
          Object.keys(payload || {})
        );
        throw new Error("ID do usuário não encontrado no token");
      }

      const roles = payload?.roles || payload?.role || "";

      localStorage.setItem(TOKEN_KEY, `${data.access_token}`);

      const user: IUserData = {
        id: userId,
        name: payload?.name || "",
        roles: roles,
        lastLogin: dayjs().format("YYYY-MM-DD"),
      };

      localStorage.setItem(USER_DATA, JSON.stringify(user));

      window.location.href = "/";

      return Promise.resolve("/");
    } catch (error: any) {
      if (error.response?.status === 401) {
        return Promise.reject({ message: "Credenciais inválidas" });
      }
      return Promise.reject({
        message:
          error.response?.data?.detail ||
          error.message ||
          "Erro ao fazer login",
      });
    }
  },
  logout: () => {
    handleLogout();
    return Promise.resolve("/login");
  },
  checkError: (error) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      handleLogout();
      return Promise.reject();
    }
    return Promise.resolve();
  },
  checkAuth: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userdata = localStorage.getItem(USER_DATA);

    if (!token || !userdata) {
      return Promise.reject();
    }

    try {
      const [, payloadBase64] = token.split(".");
      const payload = JSON.parse(atob(payloadBase64));
      const expiration = dayjs.unix(payload.exp);
      const now = dayjs();

      if (expiration.isBefore(now)) {
        handleLogout();
        return Promise.reject("/login");
      }

      const user: IUserData = JSON.parse(userdata);
      const today = now.format("YYYY-MM-DD");

      if (today !== user.lastLogin) {
        handleLogout();
        return Promise.reject();
      }
    } catch (error) {
      handleLogout();
      return Promise.reject();
    }

    return Promise.resolve();
  },
  getPermissions: () => {
    const userdata = localStorage.getItem(USER_DATA);
    if (userdata) {
      const parsedUser = JSON.parse(userdata);
      return Promise.resolve(parsedUser.roles);
    }
    return Promise.reject();
  },
  getUserIdentity: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userdata = localStorage.getItem(USER_DATA);
    if (!token || !userdata) {
      return Promise.reject();
    }

    const user: IUserData = JSON.parse(userdata);

    return Promise.resolve({
      id: user.id,
      name: user.name,
      roles: user.roles,
      avatar: "/images/user-icon.png",
    });
  },
});
